import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

/*
  Page-turn animation:
  - Next image sits underneath, fully visible
  - Current image is a "page" that folds away clockwise from the left spine
  - Uses CSS 3D perspective + rotateY on the page layer
  - Shadow on the turning page + gradient reveal on the next image
*/

const CustomerShowcase = () => {
  const [cards, setCards]     = useState([]);
  const [imgIdx, setImgIdx]   = useState({});   // current (front) image index
  const [nextIdx, setNextIdx] = useState({});   // next (underneath) image index
  const [turning, setTurning] = useState({});   // is card mid-turn?
  const intervalRefs = useRef({});
  const turningRef   = useRef({});              // mirror of turning state for interval closure

  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const r = await api.get('/customer-showcase');
      const first4 = r.data.slice(0, 4);
      setCards(first4);
      const initIdx = {}, initNext = {}, initTurn = {};
      first4.forEach((card, i) => {
        const imgs = getImages(card);
        initIdx[i]  = 0;
        initNext[i] = imgs.length > 1 ? 1 : 0;
        initTurn[i] = false;
      });
      setImgIdx(initIdx);
      setNextIdx(initNext);
      setTurning(initTurn);
    } catch (e) { console.error(e); }
  };

  const getImages = (card) =>
    Array.isArray(card.images) ? card.images : [card.image1, card.image2].filter(Boolean);

  // Trigger a page-turn for one card
  const triggerTurn = (cardIdx, totalImages) => {
    if (totalImages <= 1) return;
    if (turningRef.current[cardIdx]) return; // already turning

    turningRef.current[cardIdx] = true;
    setTurning(prev => ({ ...prev, [cardIdx]: true }));

    // After turn animation completes (1.1s), snap current → next, prepare new next
    setTimeout(() => {
      setImgIdx(prev => {
        const newCurrent = (prev[cardIdx] + 1) % totalImages;
        setNextIdx(n => ({ ...n, [cardIdx]: (newCurrent + 1) % totalImages }));
        return { ...prev, [cardIdx]: newCurrent };
      });
      setTurning(prev => ({ ...prev, [cardIdx]: false }));
      turningRef.current[cardIdx] = false;
    }, 1100);
  };

  // Auto-cycle each card with staggered start
  useEffect(() => {
    if (cards.length === 0) return;

    cards.forEach((card, cardIdx) => {
      const images = getImages(card);
      if (images.length <= 1) return;

      const delay = cardIdx * 900; // stagger so they don't all turn together

      const t = setTimeout(() => {
        triggerTurn(cardIdx, images.length);
        intervalRefs.current[cardIdx] = setInterval(() => {
          triggerTurn(cardIdx, images.length);
        }, 3000); // new page every 3s
      }, delay);

      intervalRefs.current[`d${cardIdx}`] = t;
    });

    return () => {
      cards.forEach((_, i) => {
        clearInterval(intervalRefs.current[i]);
        clearTimeout(intervalRefs.current[`d${i}`]);
      });
    };
  }, [cards]);

  if (cards.length === 0) return null;

  return (
    <section className="py-10 md:py-16 lg:py-24 bg-[#FFFDF9]" data-testid="customer-showcase-section">
      <div className="container mx-auto px-3 md:px-6 lg:px-8">

        <h2 className="font-heading text-lg md:text-xl lg:text-2xl font-bold text-[#2A0000] text-center mb-2 tracking-[0.25em] md:tracking-[0.3em] uppercase">
          OUR CUSTOMERS
        </h2>
        <p className="text-center text-xs text-gray-400 mb-8 md:mb-12 tracking-wide">
          Our happy customers
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
          {cards.map((card, cardIdx) => {
            const images  = getImages(card);
            const cur     = imgIdx[cardIdx]  ?? 0;
            const nxt     = nextIdx[cardIdx] ?? (images.length > 1 ? 1 : 0);
            const isTurning = !!turning[cardIdx];

            return (
              <div
                key={cardIdx}
                className={cardIdx >= 2 ? 'hidden lg:block' : ''}
                style={{
                  aspectRatio: '3/4',
                  position: 'relative',
                  background: '#111',
                  overflow: 'hidden',
                  // Perspective on the container gives the 3D depth
                  perspective: '1200px',
                }}
              >

                {/* ── LAYER 1 (bottom): next image — always fully visible underneath ── */}
                <img
                  src={images[nxt]}
                  alt={`look ${nxt + 1}`}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'top center',
                    display: 'block',
                  }}
                />

                {/* Sheen that sweeps across the next image as the page lifts — "reveal" glow */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.0) 100%)',
                  opacity: isTurning ? 1 : 0,
                  transition: isTurning ? 'opacity 0.4s ease 0.3s' : 'opacity 0.3s ease',
                  pointerEvents: 'none', zIndex: 1,
                }} />

                {/* ── LAYER 2 (top): current image — the "page" that turns ── */}
                <div
                  style={{
                    position: 'absolute', inset: 0,
                    transformOrigin: 'left center',         // spine is on the LEFT
                    transformStyle: 'preserve-3d',
                    // When turning: rotateY(-180deg) = fold backward (clockwise viewed from top)
                    transform: isTurning ? 'rotateY(-180deg)' : 'rotateY(0deg)',
                    transition: isTurning
                      ? 'transform 1.1s cubic-bezier(0.45, 0.05, 0.55, 0.95)'
                      : 'none',
                    zIndex: 2,
                  }}
                >
                  {/* Front face of the page — the current image */}
                  <img
                    src={images[cur]}
                    alt={`look ${cur + 1}`}
                    style={{
                      position: 'absolute', inset: 0,
                      width: '100%', height: '100%',
                      objectFit: 'cover', objectPosition: 'top center',
                      display: 'block',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                    }}
                  />

                  {/* Page-curl shadow — darkens the right edge as the page bends */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 40%)',
                    opacity: isTurning ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    pointerEvents: 'none',
                  }} />

                  {/* Back face of the page — shown as it folds past 90° (slightly translucent paper look) */}
                  <div
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(240,235,228,0.15)',
                      backfaceVisibility: 'visible',
                      WebkitBackfaceVisibility: 'visible',
                      transform: 'rotateY(180deg)',
                    }}
                  />
                </div>

                {/* ── Dot indicators ── */}
                {images.length > 1 && (
                  <div style={{
                    position: 'absolute', bottom: 10, left: 0, right: 0,
                    display: 'flex', justifyContent: 'center', gap: 5,
                    pointerEvents: 'none', zIndex: 5,
                  }}>
                    {images.map((_, dotIdx) => (
                      <div key={dotIdx} style={{
                        width:  dotIdx === cur ? 16 : 5,
                        height: 5,
                        borderRadius: 999,
                        background: dotIdx === cur ? '#fff' : 'rgba(255,255,255,0.45)',
                        transition: 'width 0.4s ease',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
                      }} />
                    ))}
                  </div>
                )}

                {/* Bottom gradient for readability */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 70,
                  background: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent)',
                  pointerEvents: 'none', zIndex: 4,
                }} />

              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default CustomerShowcase;